import { useEffect, useRef, useState } from "react";
import { ProductPriceDetails } from "../types/pricing/product-price-details.types";
import { calculateEvaluatedQuantity, getNonQtySelectionsKey, resolveStablePriceDetails } from "../util/price-extraction-helper";
import { usePricingDetails } from "./usePricingDetails";

interface UseStablePriceDetailsParams {
  priceQueryId: string;
  quantity: string | undefined;
  selections: Record<string, string> | undefined;
}

interface UseStablePriceDetailsResult {
  priceDetails: ProductPriceDetails | undefined;
  evaluatedQuantity: string;
  isCurrentError: boolean;
}

/**
 * Returns stable price details that prevent layout shift during color/attribute switches.
 *
 * During a color switch, quantity temporarily becomes undefined and a range-price response
 * arrives before the qty-specific one. This hook retains the last valid qty-based price
 * until the new qty-specific response arrives, preventing a jarring range → qty layout shift.
 *
 * When the user explicitly clears the quantity (without changing other selections), the hook
 * allows the range price to show immediately.
 */
export function useStablePriceDetails({
  priceQueryId,
  quantity,
  selections,
}: UseStablePriceDetailsParams): UseStablePriceDetailsResult {
  const [lastValidQuantity, setLastValidQuantity] = useState<string | undefined>(quantity);
  const [lastValidPriceQueryId, setLastValidPriceQueryId] = useState<string | undefined>(undefined);

  // Track whether quantity was explicitly cleared by the user (vs temporarily undefined during color switch).
  const prevQuantityRef = useRef<string | undefined>(quantity);
  const prevNonQtySelectionsRef = useRef<string>("");
  const [userClearedQty, setUserClearedQty] = useState(false);

  const currentNonQtySelections = getNonQtySelectionsKey(selections);

  useEffect(() => {
    const prevQty = prevQuantityRef.current;
    const prevNonQty = prevNonQtySelectionsRef.current;
    const qtyWasCleared = prevQty && Number(prevQty) > 0 && (!quantity || Number(quantity) <= 0);
    const nonQtySelectionsChanged = prevNonQty !== currentNonQtySelections;

    if (qtyWasCleared && !nonQtySelectionsChanged) {
      // Only qty changed (user cleared it) — show range
      setUserClearedQty(true);
      setLastValidQuantity(undefined);
    } else if (qtyWasCleared && nonQtySelectionsChanged) {
      // Color/other attribute changed alongside qty disappearing — color switch, not user-initiated clear
      setUserClearedQty(false);
    } else if (quantity && Number(quantity) > 0) {
      // Qty is set to a positive value — reset the cleared flag
      setUserClearedQty(false);
    }

    prevQuantityRef.current = quantity;
    prevNonQtySelectionsRef.current = currentNonQtySelections;
  }, [quantity, currentNonQtySelections]);

  const currentPriceDetails = usePricingDetails(priceQueryId);
  const lastValidPriceDetails = usePricingDetails(lastValidPriceQueryId ?? "");

  // Update cached key/quantity when we have valid price details
  useEffect(() => {
    const priceForCurrentQuantityExists =
      quantity &&
      currentPriceDetails?.prices?.estimatedPrices?.[Number(quantity)];

    if (priceForCurrentQuantityExists) {
      setLastValidPriceQueryId(priceQueryId);
      setLastValidQuantity(quantity);
    }
  }, [currentPriceDetails, priceQueryId, quantity]);

  // Decide which priceDetails to use: current if available, else cached fallback.
  const priceDetails = resolveStablePriceDetails({
    currentPriceDetails,
    lastValidPriceDetails,
    lastValidQuantity,
    quantity,
    userClearedQty,
  });

  const evaluatedQuantity = calculateEvaluatedQuantity(priceDetails, quantity, lastValidQuantity);

  return { priceDetails, evaluatedQuantity, isCurrentError: !!currentPriceDetails?.isError };
}
